import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';

import { useFeatures } from '@atc/features';

import { PageSection } from 'reaxl';

import { vdpResultsDuck } from '@/ducks/vdp';

import Anchor from '@/components/Anchor/index.js';
import LazyComponent from '@/components/LazyComponent';

const FraudReportContainer = dynamic(() => import('@/containers/FraudReportContainer'), { ssr: false });

const AdditionalResourcesContainer = dynamic(() => import('@/containers/vdp/AdditionalResourcesContainer'), { ssr: false });
const HomeServicesContainer = dynamic(() => import('@/containers/vdp/HomeServicesContainer'), { ssr: false });
const OwnerDetailsContainer = dynamic(() => import('@/containers/vdp/OwnerDetailsContainer'), { ssr: false });
const PreorderSearchContainer = dynamic(() => import('@/containers/vdp/PreorderSearchContainer'), { ssr: false });
const VehicleExchangeContainer = dynamic(() => import('@/containers/vdp/VehicleExchangeContainer'), { ssr: false });
const SimilarInventoryContainer = dynamic(() => import('@/containers/vdp/SimilarInventoryContainer'), { ssr: false });

const PsxLowerDetails = dynamic(() => import('@/containers/vdp/psx/PsxLowerDetails'), { ssr: false });

/**
 * Container component for rendering the lower section of the Vehicle Details Page (VDP)
 * This includes all components below where the sticky sidebar stops.
 *
 * Renders the following components conditionally based on feature flags and vehicle data:
 * - Owner details (contact information, dealer hours, etc.)
 * - Vehicle exchange section (if applicable)
 * - Preorder search card (for custom orders)
 * - Home services
 * - Additional similar inventory carousel (feature flag dependent)
 * - Additional resources (feature flag dependent)
 * - Fraud report functionality
 *
 * @param {Object} props - Component props
 * @param {Function} props.onEmailCTAClick - Callback function for email CTA click events
 * @param {Function} props.chatClickHandler - Callback function for chat CTA click events
 * @param {Function} props.phoneAndTextHandler - Callback function for phone and text CTA click events
 * @returns {React.ReactElement} The rendered component
 */
function LowerDetails({
    onEmailCTAClick,
    chatClickHandler,
    phoneAndTextHandler,
}) {
    const {
        additional_resources: [isAdditionalResourcesEnabled],
        vdp_enable_second_vehicles_carousel: [isVdpEnableSecondVehiclesCarousel],
        similar_inventory: [, { eligible_for_non_dealer_inventory: eligibleForNonDealerInventory }],
    } = useFeatures([
        'additional_resources',
        'vdp_enable_second_vehicles_carousel',
        'similar_inventory',
    ]);
    const isSimilarListingsCarouselEnabled = isVdpEnableSecondVehiclesCarousel && eligibleForNonDealerInventory;

    const {
        hasVehicleExchange,
        owner: {
            customOrderStatus: { newCarCustomOrder, newCarBoost } = {},
            privateSeller: isPrivateSeller,
        } = {},
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};

    const showPreorderCard = newCarCustomOrder || newCarBoost;

    if (isPrivateSeller) {
        return <PsxLowerDetails />;
    }

    return (
        <>
            <LazyComponent key="owner_details_lazy">
                <OwnerDetailsContainer
                    chatClickHandler={chatClickHandler}
                    phoneClickHandler={phoneAndTextHandler}
                    textClickHandler={phoneAndTextHandler}
                    onEmailCTAClick={onEmailCTAClick}
                />
            </LazyComponent>

            {hasVehicleExchange && (
                <LazyComponent key="vehicle_exchange">
                    <VehicleExchangeContainer />
                </LazyComponent>
            )}

            {showPreorderCard && <LazyComponent key="preorder_lazy"><PreorderSearchContainer /></LazyComponent>}

            <LazyComponent
                fallback={<Anchor id="homeServices" />}
                key="ahs"
            >
                <HomeServicesContainer
                    onEmailCTAClick={onEmailCTAClick}
                />
            </LazyComponent>

            {isSimilarListingsCarouselEnabled && (
                <LazyComponent key="second_recommended_inventory">
                    <PageSection
                        alignment="left"
                        className="col-lg-8"
                    >
                        <SimilarInventoryContainer isSimilarDealerInventoryOnly />
                    </PageSection>
                </LazyComponent>
            )}

            {isAdditionalResourcesEnabled && <LazyComponent key="addl_resources"><AdditionalResourcesContainer /></LazyComponent>}

            <LazyComponent key="fraud"><FraudReportContainer /></LazyComponent>
        </>
    );
}

LowerDetails.propTypes = {
    /**
     * The callback function fires when email CTA is clicked
     */
    onEmailCTAClick: PropTypes.func,
    /**
     * The callback function fires when chat CTA is clicked
     */
    chatClickHandler: PropTypes.func,
    /**
     * The callback function fires when owner phone and text CTA is called
     */
    phoneAndTextHandler: PropTypes.func,
};

/**
 * Export memoized version of the LowerDetails component to prevent unnecessary re-renders
 * when parent components update but LowerDetails props remain unchanged
 */
export default memo(LowerDetails);
